import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getBookings } from '../services/BookingService';
import { BookingResponseModel } from '../types/BookingModels';
import BookingList from '../components/BookingList';
import { NavigationWrapper } from '../components/navigation/NavigationWrapper';
import {
  Box,
  Button,
  Paper,
  ScrollArea,
  Stack,
  Tabs,
  Title,
} from '@mantine/core';
import { useApp } from '../context/AppContext';
import { primaryColor } from '../constants/theme';
import BookingItemSkeletonLoader from './new/components/bookings/SkeletonLoader';
import { useQuery } from '@tanstack/react-query';
import { IconAlertTriangle } from '@tabler/icons-react';
import { BookingDetailsModal } from './new/components/bookings/BookingDetailsModal';

export const Bookings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [upcomingBookings, setUpcomingBookings] = useState<
    BookingResponseModel[]
  >([]);

  const [pastBookings, setPastBookings] = useState<BookingResponseModel[]>([]);
  const { scrollAreaHeight, token, profile, isTabletAndBelow } = useApp();
  const scrollContainerHeight =
    Number(scrollAreaHeight.replace('px', '')) - 100;
  const { isLoading, isError, refetch, isFetching, isRefetching } = useQuery<
    BookingResponseModel[]
  >({
    enabled: !!token,
    queryKey: ['bookings'],
    queryFn: async () => {
      const bookingsResponse = await getBookings(token);
      if (bookingsResponse) {
        const now = new Date();
        const upcoming: BookingResponseModel[] = [];
        const past: BookingResponseModel[] = [];

        bookingsResponse.bookings.forEach((booking) => {
          const bookingEndTime = new Date(booking.endIso);
          if (bookingEndTime > now) {
            upcoming.push(booking);
          } else {
            past.push(booking);
          }
        });

        // Sort upcoming bookings in ascending order
        upcoming.sort(
          (a, b) => new Date(a.endIso).getTime() - new Date(b.endIso).getTime()
        );

        // Sort past bookings in descending order
        past.sort(
          (a, b) => new Date(b.endIso).getTime() - new Date(a.endIso).getTime()
        );

        setUpcomingBookings(upcoming);
        setPastBookings(past);
      }
      return bookingsResponse?.bookings ?? [];
    },
  });

  if (isError) {
    return (
      <NavigationWrapper disableScroll>
        <Stack justify="top" align="center" h={scrollAreaHeight}>
          <Box
            bg={'white'}
            p={20}
            w={isTabletAndBelow ? '100%' : 1200}
            h={scrollAreaHeight}
          >
            <Stack align="center" justify="center">
              <IconAlertTriangle size={60} color={primaryColor} />
              <Title
                order={4}
                fw="normal"
                data-testid="unable-to-fetch-booking-message"
              >
                Error fetching bookings.
              </Title>
              <Button
                color={primaryColor}
                w={200}
                loading={isRefetching}
                onClick={() => refetch()}
              >
                Retry
              </Button>
            </Stack>
          </Box>
        </Stack>
      </NavigationWrapper>
    );
  }

  return (
    <NavigationWrapper disableScroll>
      <Stack align="center">
        <Paper
          maw={1200}
          shadow="md"
          p={{ base: 'md', sm: 'lg' }}
          radius="md"
          withBorder
          w="100%"
          mih={isTabletAndBelow ? scrollAreaHeight : 'auto'}
        >
          <Tabs defaultValue="upcoming" color={primaryColor} variant="pills">
            <Tabs.List
              style={(theme) => ({
                border: `1px solid ${theme.colors.gray[3]}`,
                borderRadius: theme.radius.sm,
                padding: 2,
                gap: 0,
              })}
            >
              <Tabs.Tab
                value="upcoming"
                style={{ flex: 1, borderRadius: 'var(--mantine-radius-sm)' }}
                data-testid="subtitle-upcoming-bookings"
              >
                Upcoming
              </Tabs.Tab>
              <Tabs.Tab
                value="past"
                style={{ flex: 1, borderRadius: 'var(--mantine-radius-sm)' }}
                data-testid="subtitle-past-bookings"
              >
                Past
              </Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="upcoming" pt="xs" keepMounted>
              <ScrollArea h={scrollContainerHeight}>
                {isLoading || isFetching || isRefetching ? (
                  <Stack gap={10} data-testid="bookings-loader">
                    {Array.from({ length: 10 }).map((_, index) => (
                      <BookingItemSkeletonLoader key={index} />
                    ))}
                  </Stack>
                ) : (
                  <BookingList
                    profile={profile}
                    isPastBooking={false}
                    bookings={upcomingBookings}
                    refreshBookings={() => refetch()}
                  />
                )}
              </ScrollArea>
            </Tabs.Panel>
            <Tabs.Panel value="past" pt="xs" keepMounted>
              <ScrollArea h={scrollContainerHeight}>
                {isLoading ? (
                  <Stack>
                    {Array.from({ length: 8 }).map((_, index) => (
                      <BookingItemSkeletonLoader key={index} />
                    ))}
                  </Stack>
                ) : (
                  <BookingList
                    isPastBooking={true}
                    bookings={pastBookings}
                    profile={profile}
                    refreshBookings={() => refetch()}
                  />
                )}
              </ScrollArea>
            </Tabs.Panel>
          </Tabs>
        </Paper>
      </Stack>
    </NavigationWrapper>
  );
};
